import { Link, NavLink, useNavigate } from "react-router-dom";
import { ShoppingBag, User, Search, Menu, LogOut, Shield, Heart, MapPin } from "lucide-react";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { useWishlist } from "@/context/WishlistContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";

const navLinks = [
  { to: "/shop", label: "Shop All" },
  { to: "/shop?category=home-decors", label: "Home Decors" },
  { to: "/shop?category=accessories", label: "Accessories" },
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const { count } = useCart();
  const wishlist = useWishlist();
  const [query, setQuery] = useState("");
  const navigate = useNavigate();

  const onSearch = (e) => {
    e.preventDefault();
    if (query.trim()) navigate(`/shop?search=${encodeURIComponent(query.trim())}`);
  };

  return (
    <header
      className="sticky top-0 z-50 backdrop-blur-xl bg-ivory/80 border-b border-deepbrown/10"
      data-testid="main-navbar"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-4 flex items-center gap-6">
        {/* Mobile menu */}
        <Sheet>
          <SheetTrigger className="md:hidden" data-testid="mobile-menu-trigger">
            <Menu className="h-6 w-6 text-deepbrown" />
          </SheetTrigger>
          <SheetContent side="left" className="bg-ivory flex flex-col">
            <div className="mt-8 flex flex-col gap-4 flex-1">
              {navLinks.map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  className="font-display text-xl text-deepbrown hover:text-rani transition-colors"
                  data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {l.label}
                </Link>
              ))}
            </div>
            {/* Auth actions pinned to bottom of drawer */}
            <div className="border-t border-deepbrown/10 pt-4 pb-2">
              {user ? (
                <>
                  <p className="text-xs uppercase tracking-widest text-deepbrown/50 font-bold mb-3">
                    Signed in as {user.name.split(" ")[0]}
                  </p>
                  <button
                    onClick={logout}
                    className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-full bg-rani text-white text-sm font-bold hover:bg-rani/90 transition-colors"
                    data-testid="mobile-menu-logout"
                  >
                    <LogOut className="h-4 w-4" /> Log Out
                  </button>
                </>
              ) : (
                <Link
                  to="/login"
                  className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-full bg-deepbrown text-ivory text-sm font-bold hover:bg-rani transition-colors"
                  data-testid="mobile-menu-signin"
                >
                  <User className="h-4 w-4" /> Sign In
                </Link>
              )}
            </div>
          </SheetContent>
        </Sheet>

        {/* Logo */}
        <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="navbar-logo">
          <img
            src="https://customer-assets.emergentagent.com/job_yarn-market-in/artifacts/1x2gpo9a_ChatGPT%20Image%20Jun%2029%2C%202026%2C%2009_03_28%20PM.png"
            alt="Sutrakosh by Chanchala — Handmade Treasures, Woven With Love"
            className="h-14 md:h-16 w-auto object-contain"
            data-testid="navbar-logo-img"
          />
        </Link>

        {/* Nav links */}
        <nav className="hidden md:flex items-center gap-6 ml-4">
          {navLinks.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              className="font-body text-sm font-semibold text-deepbrown/80 hover:text-rani transition-colors"
              data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, '-')}`}
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex-1" />

        {/* Search */}
        <form onSubmit={onSearch} className="hidden lg:flex items-center bg-white rounded-full px-4 py-2 border border-deepbrown/10 w-64">
          <Search className="h-4 w-4 text-deepbrown/60" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search yarn goodies..."
            className="bg-transparent ml-2 outline-none text-sm flex-1"
            data-testid="search-input"
          />
        </form>

        {/* Account */}
        {user ? (
          <DropdownMenu>
            <DropdownMenuTrigger
              className="h-10 w-10 rounded-full bg-marigold/20 hover:bg-marigold/40 grid place-items-center transition-colors"
              data-testid="user-menu-trigger"
            >
              <User className="h-5 w-5 text-deepbrown" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-white border-deepbrown/10">
              <DropdownMenuLabel className="font-display">Hi, {user.name.split(" ")[0]}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild>
                <Link to="/account" data-testid="menu-account">My Orders</Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/wishlist" data-testid="menu-wishlist" className="flex items-center gap-2">
                  <Heart className="h-4 w-4" /> Wishlist {wishlist?.count > 0 && <span className="text-rani">({wishlist.count})</span>}
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/addresses" data-testid="menu-addresses" className="flex items-center gap-2">
                  <MapPin className="h-4 w-4" /> Addresses
                </Link>
              </DropdownMenuItem>
              {user.role === "admin" && (
                <DropdownMenuItem asChild>
                  <Link to="/admin" data-testid="menu-admin" className="flex items-center gap-2">
                    <Shield className="h-4 w-4" /> Admin Panel
                  </Link>
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={logout} data-testid="menu-logout" className="text-rani">
                <LogOut className="h-4 w-4 mr-2" /> Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : (
          <Link
            to="/login"
            className="inline-flex items-center gap-2 px-3 sm:px-4 py-2 rounded-full bg-deepbrown text-ivory text-sm font-semibold hover:bg-rani transition-colors"
            data-testid="nav-login-btn"
          >
            <User className="h-4 w-4" /> <span className="hidden xs:inline sm:inline">Sign In</span>
          </Link>
        )}

        {/* Cart */}
        <Link
          to="/cart"
          className="relative h-10 w-10 rounded-full bg-rani text-white grid place-items-center hover:bg-rani/90 transition-colors shadow-md"
          data-testid="nav-cart-btn"
        >
          <ShoppingBag className="h-5 w-5" />
          {count > 0 && (
            <span
              className="absolute -top-1 -right-1 h-5 min-w-5 px-1 rounded-full bg-marigold text-deepbrown text-[10px] font-bold grid place-items-center border-2 border-ivory"
              data-testid="cart-count"
            >
              {count}
            </span>
          )}
        </Link>
      </div>
    </header>
  );
}
